'use client';

import type { CSSProperties, ReactNode } from 'react';
import styles from './skeleton.module.css';
import { useT } from '@/lib/locale-context';

export interface SkeletonProps {
  /** `line` stands in for a line of text (the default); `block` for a box: a
   *  chip, a control, an avatar. */
  shape?: 'line' | 'block';
  /** A number is a percentage of the container's width; a string is any CSS
   *  length or token (`var(--control-h-md)`). A line defaults to the full
   *  width, a block to its own height (a square). */
  width?: number | string;
  /** A CSS length or token. A line defaults to 0.8em of the type it sits in,
   *  with the leading around it, so it takes one text line's height; a block
   *  defaults to the small control height (`--control-h-sm`). */
  height?: string;
  className?: string;
}

/**
 * Skeleton - a placeholder for content that has not arrived yet (a page between
 * the tap and its render). A faint fill on a muted surface token with a slow
 * pulse that `prefers-reduced-motion` stops. Always `aria-hidden`: the shapes
 * say nothing to assistive tech, the surface says it once through
 * {@link SkeletonRegion}.
 *
 * Not a busy indicator: a control that is working shows a `Spinner`; a
 * Skeleton stands in for content (docs/DESIGN_SYSTEM.md, "Component library").
 * No hooks, so a server component (a route's `loading.tsx`) renders it as is.
 */
export function Skeleton({ shape = 'line', width, height, className }: SkeletonProps) {
  const style: CSSProperties = {};
  if (width !== undefined) style.width = typeof width === 'number' ? `${width}%` : width;
  else if (shape === 'block') style.width = height ?? 'var(--control-h-sm)';
  if (height !== undefined) style.height = height;
  return (
    <span
      className={[styles.skeleton, styles[shape], className].filter(Boolean).join(' ')}
      style={style}
      aria-hidden
    />
  );
}

/**
 * The region a surface's skeleton renders in: `role="status"` with one
 * accessible name ("Loading" unless the caller names it), once per surface and
 * never per shape. Layout belongs to the caller (`className`), so the region
 * can be the surface's own frame and nothing moves when the content arrives.
 */
export function SkeletonRegion({
  label,
  className,
  testId,
  children,
}: {
  label?: string;
  className?: string;
  testId?: string;
  children: ReactNode;
}) {
  const t = useT();
  return (
    <div
      role="status"
      aria-label={label ?? t('kit.skeleton.loading')}
      className={className}
      data-testid={testId}
    >
      {children}
    </div>
  );
}
